import type { Mode } from "@trip/domain/danger";
import { LEVEL_COLORS } from "../lib/danger-sectors.ts";

/** Each danger level's name, level 1 first: the legend's words, and the place view's. */
export const LEVEL_WORDS = ["Calmest", "Low", "Moderate", "Raised", "High", "Highest"] as const;
export const MODE_WORDS: Record<Mode, string> = { FOOT: "walking", VEHICLE: "riding" };

/** The hour as the legend prints it: always two digits, wrapped into the day. */
export const hourLabel = (hour: number) =>
  `${String(((Math.floor(hour) % 24) + 24) % 24).padStart(2, "0")}:00`;

/**
 * The credit as one short line, for the phone, in the map's credit and in the zone panel: the
 * snapshot's first sentence, without the address in brackets, and "licensed X" as
 * "· X". The snapshot's "Crime data: Hoyo de Crimen (hoyodecrimen.com), licensed CC-BY. Any
 * screen that shows the danger map must credit it." becomes "Crime data: Hoyo de Crimen · CC-BY":
 * the second sentence is a note to whoever builds the screen, not words for the traveller.
 * Read from the attribution rather than written here, so a new source still gets its own name.
 */
export const shortCredit = (attribution: string) =>
  (attribution.trim().split(/\.\s/)[0] ?? "")
    .replace(/\.$/, "")
    .replace(/\s*\([^)]*\)/g, "")
    .replace(/,\s*licensed\s+/i, " · ")
    .trim() || attribution;

/** What the page says in place of a legend when the layer was switched on and has no data. */
export const UNAVAILABLE_MESSAGE =
  "Safety data unavailable — this map is not showing danger levels.";

/**
 * The danger map's legend and the credit its data's licence requires. The colours themselves are
 * painted by the map from the zones the page hands it; this is the words around them.
 *
 * The page renders this for a trip whose switch is on, once the snapshot has either arrived or
 * failed to. A switched-off trip renders nothing at all. With no data (`attribution` null) it
 * says so in one line, inside the same labelled region, instead of vanishing: a layer that was
 * asked for and has gone quiet reads as "fine", which is the one thing a safety layer must not do.
 *
 * Somewhere with no sector (three small gaps in the city's boundaries are real) is simply not
 * painted. The legend names that, so an unpainted patch reads as "no data" and not as "safe".
 */
export function DangerLayer({
  mode,
  hour,
  attribution,
  onMode,
}: {
  mode: Mode;
  /**
   * The clock hour the colours were computed for. It is on the heading because it changes them a
   * great deal: at 03:00 a hand-set ceiling puts 60 of 72 sectors at level 5 or 6, and a legend
   * that said only "walking" would read as the daytime picture.
   */
  hour: number;
  /**
   * Read from the snapshot itself, so the wording can never drift from the asset. Null means
   * there is no snapshot to draw, and the layer says so.
   */
  attribution: string | null;
  /** Absent where something else decides the mode (on the phone, its zone watcher). */
  onMode?: ((mode: Mode) => void) | undefined;
}) {
  if (attribution === null) {
    return (
      <section className="danger-legend" aria-label="Danger map">
        <p>{UNAVAILABLE_MESSAGE}</p>
      </section>
    );
  }
  return (
    <section className="danger-legend" aria-label="Danger map">
      <h3>
        Danger level, {MODE_WORDS[mode]}
        {Number.isFinite(hour) ? ` · adjusted for ${hourLabel(hour)}` : ""}
      </h3>
      <ol className="plain" aria-label="Levels">
        {LEVEL_COLORS.map((color, i) => (
          <li key={color}>
            <span className="danger-swatch" style={{ background: color }} aria-hidden="true" />
            {i + 1} · {LEVEL_WORDS[i]}
          </li>
        ))}
        <li>
          <span className="danger-swatch none" aria-hidden="true" />
          No data
        </li>
      </ol>
      {onMode && (
        <div role="group" aria-label="Colours are for">
          {(["FOOT", "VEHICLE"] as const).map((m) => (
            <button key={m} type="button" aria-pressed={m === mode} onClick={() => onMode(m)}>
              {m === "FOOT" ? "On foot" : "Riding"}
            </button>
          ))}
        </div>
      )}
      <p className="muted small">{attribution}</p>
    </section>
  );
}
